import React, { useEffect, useState } from 'react';
import { ChevronDown, ChevronRight, CalendarDays } from 'lucide-react';
import BaseTable from '../base/BaseTable';
import { useReports } from '../../hooks/useReports';
import { formatDate } from '../../utils/formatDate';
import { renderUrgencyBadge, getStatusStyle } from '../../utils/badgeUtils';

const formatThang = (thang) => {
    // thang: "YYYY-MM" -> "MM/YYYY"
    if (!thang || typeof thang !== 'string' || !thang.includes('-')) return thang || '';
    const [nam, thg] = thang.split('-');
    return `${thg}/${nam}`;
};

const renderStatus = (value) => {
    if (!value) {
        return <span className="text-sm text-gray-400">-</span>;
    }
    const style = getStatusStyle(value);
    return (
        <span
            className="inline-flex items-center gap-1 px-3 py-1 text-xs font-medium rounded-full"
            style={{ backgroundColor: style.bg, color: style.color }}
        >
            {value}
        </span>
    );
};

const columns = [
    {
        title: 'Mã phản ánh',
        dataIndex: 'ma_phan_anh',
        key: 'ma_phan_anh',
        width: '130px',
        render: (value) => (
            <span className="text-sm font-medium text-gray-900">{value || '-'}</span>
        )
    },
    {
        title: 'Tiêu đề',
        dataIndex: 'tieu_de',
        key: 'tieu_de',
        width: '240px',
        render: (value) => (
            <span className="block max-w-[240px] truncate text-sm text-gray-900" title={value}>
                {value || '-'}
            </span>
        )
    },
    {
        title: 'Nội dung',
        dataIndex: 'mo_ta',
        key: 'mo_ta',
        width: '240px',
        render: (value) => (
            <span className="block max-w-[240px] truncate text-sm text-gray-700" title={value}>
                {value || '-'}
            </span>
        )
    },
    {
        title: 'Lĩnh vực',
        dataIndex: 'linh_vuc_phan_anh',
        key: 'linh_vuc_phan_anh',
        width: '160px',
        render: (value) => (
            <span className="block max-w-[160px] truncate text-sm text-gray-700" title={value}>
                {value || '-'}
            </span>
        )
    },
    {
        title: 'Mức độ',
        dataIndex: 'muc_do',
        key: 'muc_do',
        width: '120px',
        render: (value) => renderUrgencyBadge(value)
    },
    {
        title: 'Vị trí',
        dataIndex: 'vi_tri',
        key: 'vi_tri',
        width: '200px',
        render: (value) => (
            <span className="block max-w-[200px] truncate text-sm text-gray-700" title={value}>
                {value || '-'}
            </span>
        )
    },
    {
        title: 'Chuyên viên',
        dataIndex: 'chuyen_vien',
        key: 'chuyen_vien',
        width: '160px',
        render: (value) => (
            <span className="block max-w-[160px] truncate text-sm text-gray-700" title={value}>
                {value || '—'}
            </span>
        )
    },
    {
        title: 'Trạng thái hiện tại',
        dataIndex: 'trang_thai_hien_tai',
        key: 'trang_thai_hien_tai',
        width: '150px',
        render: (value) => renderStatus(value)
    },
    {
        title: 'Thời gian nhận',
        dataIndex: 'thoi_gian_tao',
        key: 'thoi_gian_tao',
        width: '160px',
        render: (value) => (
            <span className="text-sm text-gray-600">{value ? formatDate(value) : '-'}</span>
        )
    }
];

const PAGE_SIZE = 10;

export default function PhanAnhTheoThang({ dateRange, selectedDomain }) {
    const { phanAnhTheoThang, phanAnhTheoThangLoading, loadPhanAnhTheoThang } = useReports();
    const [collapsed, setCollapsed] = useState({});
    const [monthPages, setMonthPages] = useState({});

    useEffect(() => {
        const params = {};
        if (dateRange?.from) params.from = dateRange.from;
        if (dateRange?.to) params.to = dateRange.to;
        if (selectedDomain && selectedDomain !== 'all') {
            params.idLinhVuc = selectedDomain;
        }

        setMonthPages({});

        loadPhanAnhTheoThang(params).catch(() => {
            // lỗi đã được lưu vào state.error; bỏ qua ở đây
        });
    }, [loadPhanAnhTheoThang, dateRange?.from, dateRange?.to, selectedDomain]);

    const theoThang = phanAnhTheoThang?.theo_thang || [];
    const tongPhanAnh = phanAnhTheoThang?.tong_phan_anh || 0;

    const toggleMonth = (thang) => {
        setCollapsed((prev) => ({ ...prev, [thang]: !prev[thang] }));
    };

    const handlePageChange = (thang, page) => {
        setMonthPages((prev) => ({ ...prev, [thang]: page }));
    };

    return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 md:p-4">
            <div className="flex items-center justify-between mb-3">
                <h3 className="text-base md:text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <CalendarDays className="w-5 h-5 text-blue-600" />
                    Phản ánh theo tháng
                </h3>
                <span className="text-sm text-gray-600">
                    Tổng: <span className="font-semibold text-gray-900">{tongPhanAnh}</span> phản ánh
                </span>
            </div>

            {phanAnhTheoThangLoading ? (
                <div className="flex items-center justify-center py-10 text-gray-500">
                    Đang tải dữ liệu...
                </div>
            ) : theoThang.length === 0 ? (
                <div className="text-center py-10 text-gray-500">
                    Không có phản ánh nào trong khoảng thời gian đã chọn
                </div>
            ) : (
                <div className="space-y-3">
                    {theoThang.map((thangItem) => {
                        const isCollapsed = !!collapsed[thangItem.thang];
                        const list = thangItem.danh_sach || [];
                        const currentPage = monthPages[thangItem.thang] || 1;
                        const totalPages = Math.ceil(list.length / PAGE_SIZE) || 1;
                        const pagedData = list.slice(
                            (currentPage - 1) * PAGE_SIZE,
                            currentPage * PAGE_SIZE
                        );

                        const paginationConfig = {
                            current: currentPage,
                            pageSize: PAGE_SIZE,
                            total: list.length,
                            totalPages: totalPages,
                            onChange: (page) => handlePageChange(thangItem.thang, page),
                        };

                        return (
                            <div key={thangItem.thang} className="border border-gray-200 rounded-lg overflow-hidden">
                                <button
                                    type="button"
                                    onClick={() => toggleMonth(thangItem.thang)}
                                    className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
                                >
                                    <span className="flex items-center gap-2 font-medium text-gray-900">
                                        {isCollapsed ? (
                                            <ChevronRight className="w-4 h-4 text-gray-500" />
                                        ) : (
                                            <ChevronDown className="w-4 h-4 text-gray-500" />
                                        )}
                                        Tháng {formatThang(thangItem.thang)}
                                    </span>
                                    <span className="text-sm text-gray-600">
                                        {thangItem.tong || 0} phản ánh
                                    </span>
                                </button>

                                {!isCollapsed && (
                                    <div className="p-3">
                                        <BaseTable
                                            data={pagedData}
                                            columns={columns}
                                            showActions={false}
                                            emptyMessage="Không có phản ánh nào trong tháng này"
                                            pagination={paginationConfig}
                                            onPageChange={(page) => handlePageChange(thangItem.thang, page)}
                                        />
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
